<script setup>
import { computed } from 'vue';

const props = defineProps({
  events: { type: Array, default: () => [] },
  emptyLabel: { type: String, default: 'Nenhum evento registrado' },
  redacted: { type: Boolean, default: false },
  redactedLabel: { type: String, default: '•••• Conteúdo protegido' },
  max: { type: Number, default: 0 },
  ariaLabel: { type: String, default: 'Histórico de eventos' },
});

/**
 * LaisAuditEventList — compact append-only event list for aside panels,
 * tickets and conversation context. A lighter sibling of
 * LaisOperationalTimeline. Data-driven and redaction-aware: a redacted event
 * hides its description behind a protected placeholder and never renders a
 * payload.
 *
 * Event: { id, at, actor?, title, description?, tone?, redacted? }
 * The caller is responsible for ordering (newest-first is not enforced).
 */
const TONE_DOT = Object.freeze({
  neutral: 'bg-lais-text-muted',
  info: 'bg-lais-primary',
  positive: 'bg-lais-mint-dark',
  warning: 'bg-lais-warning',
  danger: 'bg-lais-danger',
});

const TONE_ALIASES = Object.freeze({
  info: 'info',
  active: 'info',
  positive: 'positive',
  success: 'positive',
  done: 'positive',
  warning: 'warning',
  pending: 'warning',
  danger: 'danger',
  error: 'danger',
  failed: 'danger',
});

const visibleEvents = computed(() =>
  props.max > 0 ? props.events.slice(0, props.max) : props.events
);
const hiddenCount = computed(() =>
  props.max > 0 ? Math.max(0, props.events.length - props.max) : 0
);
const moreLabel = computed(() => `+ ${hiddenCount.value}`);

const dotClass = event =>
  TONE_DOT[TONE_ALIASES[String(event.tone || '').toLowerCase()] || 'neutral'];

const isRedacted = event => Boolean(props.redacted || event.redacted);

const formatAt = value => {
  if (value === null || value === undefined || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const isoAt = value => {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};
</script>

<template>
  <div class="min-w-0">
    <ul
      v-if="visibleEvents.length"
      role="list"
      :aria-label="ariaLabel"
      class="grid gap-3"
    >
      <li
        v-for="(event, index) in visibleEvents"
        :key="event.id ?? index"
        class="grid grid-cols-[auto_minmax(0,1fr)] gap-3"
      >
        <span class="mt-1.5 flex flex-col items-center gap-1">
          <span
            class="size-2 shrink-0 rounded-full"
            :class="dotClass(event)"
            aria-hidden="true"
          />
          <span
            v-if="index !== visibleEvents.length - 1"
            class="w-px flex-1 bg-lais-border-soft"
            aria-hidden="true"
          />
        </span>
        <div class="min-w-0 pb-1">
          <div class="flex flex-wrap items-baseline justify-between gap-x-2">
            <p class="min-w-0 text-lais-body-sm font-semibold text-lais-text">
              {{ event.title }}
            </p>
            <time
              v-if="formatAt(event.at)"
              :datetime="isoAt(event.at)"
              class="shrink-0 text-lais-label-xs tabular-nums text-lais-text-muted"
            >
              {{ formatAt(event.at) }}
            </time>
          </div>
          <p v-if="event.actor" class="text-lais-body-xs text-lais-text-muted">
            {{ event.actor }}
          </p>
          <p
            v-if="isRedacted(event)"
            class="mt-0.5 select-none text-lais-body-sm italic text-lais-text-muted"
          >
            {{ redactedLabel }}
          </p>
          <p
            v-else-if="event.description"
            class="mt-0.5 text-lais-body-sm text-lais-text-body"
          >
            {{ event.description }}
          </p>
        </div>
      </li>
    </ul>

    <p
      v-else
      class="rounded-lais-sm border border-dashed border-lais-border-soft px-3 py-4 text-lais-body-sm text-lais-text-muted"
    >
      {{ emptyLabel }}
    </p>

    <p
      v-if="hiddenCount > 0"
      class="mt-2 text-lais-label-xs text-lais-text-muted"
    >
      {{ moreLabel }}
    </p>
  </div>
</template>
